import { getDocument } from '@atlaskit/browser-apis';

/**
 * GPU / virtualization custom data for Jira page-load events.
 *
 * Reads the WebGL renderer string (the most reliable "software rasterizer /
 * virtualised / VDI" signal) plus whether it was readable, and returns a flat
 * record that is merged into the page-load event's custom data by
 * `getJiraCustomData`. Keys land under `jira:gpu:*` in the fe_performance table.
 *
 * Client-only: guarded for SSR and never throws so it cannot affect a page load.
 * Pure function (no module-level state) — `getJiraCustomData` calls it per
 * page-load event; the WebGL read is cheap and the context is released
 * immediately via WEBGL_lose_context.
 */

export type GpuClass = 'hardware' | 'software' | 'virtual' | 'unknown';

export type GpuState = {
	'gpu:renderer': string;
	'gpu:vendor': string;
	'gpu:class': GpuClass;
	'gpu:unmasked': boolean;
	'gpu:webglAvailable': boolean;
};

// Substrings indicating software rendering (no real GPU acceleration).
const SOFTWARE_MARKERS = [
	'swiftshader',
	'llvmpipe',
	'softpipe',
	'software',
	'microsoft basic render',
];

// Substrings indicating a virtual GPU / hypervisor / remote display (VDI tell).
const VIRTUAL_MARKERS = [
	'vmware',
	'virtualbox',
	'parallels',
	'qemu',
	'virgl',
	'microsoft remote display',
	'microsoft hyper-v',
	'citrix',
	'red hat virtio',
];

const classifyRenderer = (renderer: string): GpuClass => {
	const r = renderer.toLowerCase();
	if (!r) {
		return 'unknown';
	}
	if (VIRTUAL_MARKERS.some((m) => r.includes(m))) {
		return 'virtual';
	}
	if (SOFTWARE_MARKERS.some((m) => r.includes(m))) {
		return 'software';
	}
	return 'hardware';
};

export const getGpuState = (): GpuState => {
	const state: GpuState = {
		'gpu:renderer': '',
		'gpu:vendor': '',
		'gpu:class': 'unknown',
		'gpu:unmasked': false,
		'gpu:webglAvailable': false,
	};

	try {
		const doc = getDocument();
		if (!doc) {
			return state;
		}

		const canvas = doc.createElement('canvas');
		// `getContext` returns the precise context type per overload, so no type
		// assertion is needed: WebGL2RenderingContext | WebGLRenderingContext.
		const gl: WebGL2RenderingContext | WebGLRenderingContext | null =
			canvas.getContext('webgl2') ?? canvas.getContext('webgl');

		if (!gl) {
			// No WebGL at all — a (weak) VM / locked-down signal.
			return state;
		}

		state['gpu:webglAvailable'] = true;

		const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
		let renderer = '';
		let vendor = '';

		if (debugInfo) {
			renderer = String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) ?? '');
			vendor = String(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) ?? '');
			state['gpu:unmasked'] = true;
		} else {
			// Privacy masking blocked the unmasked read; fall back to generic.
			renderer = String(gl.getParameter(gl.RENDERER) ?? '');
			vendor = String(gl.getParameter(gl.VENDOR) ?? '');
			state['gpu:unmasked'] = false;
		}

		state['gpu:renderer'] = renderer.slice(0, 256);
		state['gpu:vendor'] = vendor.slice(0, 128);
		state['gpu:class'] = classifyRenderer(renderer);

		const loseContext = gl.getExtension('WEBGL_lose_context');
		if (loseContext) {
			loseContext.loseContext();
		}
	} catch {
		// Never let telemetry break a page load; keep the default 'unknown' state.
	}

	return state;
};
